import { useState } from 'react'
import { Button, Checkbox, CheckboxGroup, Label, Spinner } from '@heroui/react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { Section } from '@/components/ui/data-state'
import { TextAreaFieldControl, TextFieldControl } from '@/components/ui/form-fields'
import { ImageField } from '@/features/files'
import { errorMessage } from '@/lib/api/api-error'
import { toTranslation } from '@/lib/api/common'
import type { FileRef } from '@/lib/api/common'

import { useTenantActions } from '../hooks'
import { ShippingMethod, shippingMethodLabels } from '../types'
import type { Tenant, UpdateTenantBody } from '../types'

const schema = z.object({
  nameEn: z.string().trim().min(1, 'Your business needs a name.'),
  nameKm: z.string().trim().optional(),
  descriptionEn: z.string().trim().optional(),
  descriptionKm: z.string().trim().optional(),
  addressEn: z.string().trim().optional(),
  addressKm: z.string().trim().optional(),
  contactNumber: z.string().trim().optional(),
  telegramId: z.string().trim().optional(),
  messengerLink: z.string().trim().optional(),
  telegramLink: z.string().trim().optional(),
})

type FormValues = z.infer<typeof schema>

/**
 * The business card a customer sees: name, blurb, address, how to reach you, and
 * which ways you serve.
 *
 * The two images are held outside the form. They are uploaded the moment they
 * are picked — the API takes a file id, not the bytes — so by the time Save runs
 * they are already stored and only their ids travel.
 */
export function BusinessProfileForm({ tenant }: { tenant: Tenant }) {
  const { save } = useTenantActions()

  const [logo, setLogo] = useState<FileRef | null>(tenant.logo)
  const [cover, setCover] = useState<FileRef | null>(tenant.cover)
  const [methods, setMethods] = useState<string[]>(
    tenant.configuration?.shipping?.shippingMethod ?? [],
  )
  const [isSaved, setIsSaved] = useState(false)

  const { control, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      nameEn: tenant.name?.en ?? '',
      nameKm: tenant.name?.km ?? '',
      descriptionEn: tenant.description?.en ?? '',
      descriptionKm: tenant.description?.km ?? '',
      addressEn: tenant.address?.en ?? '',
      addressKm: tenant.address?.km ?? '',
      contactNumber: tenant.contactNumber ?? '',
      telegramId: tenant.configuration?.setting?.telegramId ?? '',
      messengerLink: '',
      telegramLink: '',
    },
  })

  async function onSubmit(values: FormValues) {
    const body: UpdateTenantBody = {
      name: toTranslation(values.nameEn, values.nameKm),
      description: toTranslation(values.descriptionEn ?? '', values.descriptionKm),
      address: toTranslation(values.addressEn ?? '', values.addressKm),
      contactNumber: values.contactNumber || undefined,
      messengerLink: values.messengerLink || undefined,
      telegramLink: values.telegramLink || undefined,
      // Replaced wholesale by the server, so the untouched sections are carried
      // back over rather than dropped.
      configuration: {
        ...tenant.configuration,
        setting: { ...tenant.configuration?.setting, telegramId: values.telegramId || undefined },
        shipping: {
          ...tenant.configuration?.shipping,
          shippingMethod: methods as ShippingMethod[],
        },
      },
    }

    if (logo?.id) body.logoId = logo.id
    if (cover?.id) body.coverId = cover.id

    await save.mutateAsync(body)
    setIsSaved(true)
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onChange={() => setIsSaved(false)}
      className="flex flex-col gap-4"
    >
      <Section title="Identity" description="What customers see at the top of your storefront.">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextFieldControl control={control} name="nameEn" label="Name (English)" isRequired />
          <TextFieldControl control={control} name="nameKm" label="Name (Khmer)" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaFieldControl
            control={control}
            name="descriptionEn"
            label="Description (English)"
            rows={3}
          />
          <TextAreaFieldControl
            control={control}
            name="descriptionKm"
            label="Description (Khmer)"
            rows={3}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <ImageField label="Logo" value={logo} onChange={setLogo} aspect="square" />
          <ImageField label="Cover" value={cover} onChange={setCover} />
        </div>
      </Section>

      <Section title="Contact" description="How customers and NexQR reach you.">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextFieldControl
            control={control}
            name="contactNumber"
            label="Phone"
            type="tel"
            placeholder="012 345 678"
          />
          <TextFieldControl
            control={control}
            name="telegramId"
            label="Telegram chat ID"
            description="Where order alerts are sent."
          />
          <TextFieldControl
            control={control}
            name="messengerLink"
            label="Messenger link"
            type="url"
          />
          <TextFieldControl
            control={control}
            name="telegramLink"
            label="Telegram link"
            type="url"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <TextAreaFieldControl
            control={control}
            name="addressEn"
            label="Address (English)"
            rows={2}
          />
          <TextAreaFieldControl
            control={control}
            name="addressKm"
            label="Address (Khmer)"
            rows={2}
          />
        </div>
      </Section>

      <Section title="Ordering" description="The ways a customer can take an order from you.">
        <CheckboxGroup value={methods} onChange={setMethods} className="flex flex-col gap-2">
          <Label className="sr-only">Ordering methods</Label>
          {Object.values(ShippingMethod).map((method) => (
            <Checkbox key={method} value={method}>
              <Checkbox.Content>
                <Checkbox.Control>
                  <Checkbox.Indicator />
                </Checkbox.Control>
                <Label>{shippingMethodLabels[method]}</Label>
              </Checkbox.Content>
            </Checkbox>
          ))}
        </CheckboxGroup>
      </Section>

      {save.error && (
        <p role="alert" className="text-danger text-sm">
          {errorMessage(save.error, 'Could not save your business details.')}
        </p>
      )}

      <div className="flex items-center gap-3">
        <Button
          type="submit"
          variant="primary"
          className="h-10 rounded-full px-5 font-medium"
          isDisabled={save.isPending || formState.isSubmitting}
        >
          {save.isPending && <Spinner size="sm" />}
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>

        {isSaved && !save.isPending && <span className="text-muted text-xs">Saved.</span>}
      </div>
    </form>
  )
}
